import { useEffect, useState } from "react";
import { useNotify } from "ra-core";
import { DragDropContext, Droppable, Draggable, type DropResult } from "@hello-pangea/dnd";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronRight, Copy, Trash2, GripVertical, Plus } from "lucide-react";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Settings → Campaign / Drip builder (docs/16 §A, D29; runner D46). Admins define
// ordered multi-step campaigns; the runner (process_due_drip_enrollments) steps
// enrollments through them as DRAFTS (draft-for-approval — never auto-sent).
// Bespoke page (getSupabaseClient CRUD + shadcn); write-gated by
// manage_campaigns_automations (RLS). Assignment to status lives on Automations.

const MERGE_FIELDS = ["{{first_name}}", "{{property_address}}", "{{city}}"];
const UNITS = ["minute", "hour", "day", "week"] as const;
const ACTIONS = [
  { value: "sms", label: "SMS" }, { value: "email", label: "Email" },
  { value: "task", label: "Task" }, { value: "direct_mail", label: "Direct Mail" },
] as const;
const ENTITY_TABS = [
  { value: "lead", label: "Leads", enabled: true },
  { value: "buyer", label: "Buyers", enabled: false },
  { value: "vendor", label: "Vendors", enabled: false },
  { value: "sold", label: "Sold", enabled: false },
];
const COND_FIELDS = ["", "status", "temperature", "tag", "responded", "contactable"];
const MAIL_PIECES = ["postcard", "letter", "yellow_letter"];

type Step = {
  id?: string; step_order: number; delay_value: number; delay_unit: string; action_type: string;
  contact_scope: string; template_key: string | null; body: string | null; subject: string | null;
  task_name: string | null; task_type: string | null; task_priority: string | null; task_role_id: number | null;
  mail_piece: string | null; condition: { field?: string; op?: string; value?: string } | null;
};
type Campaign = { id: string; name: string; entity_type: string; is_active: boolean; drip_steps: Step[] };
type Tmpl = { key: string; name: string; channel: string; body: string; subject: string | null };
type Role = { id: number; name: string };

const newStep = (order: number): Step => ({
  step_order: order, delay_value: order === 1 ? 0 : 1, delay_unit: "day", action_type: "sms",
  contact_scope: "primary", template_key: null, body: "", subject: "", task_name: "", task_type: "call",
  task_priority: "medium", task_role_id: null, mail_piece: "letter", condition: null,
});

// total campaign span, humanized
const UNIT_MIN: Record<string, number> = { minute: 1, hour: 60, day: 1440, week: 10080 };
const duration = (steps: Step[]) => {
  const mins = steps.reduce((a, s) => a + (s.delay_value || 0) * (UNIT_MIN[s.delay_unit] ?? 1440), 0);
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
};
const stepSummary = (s: Step) => {
  const a = ACTIONS.find((x) => x.value === s.action_type)?.label ?? s.action_type;
  const detail = s.action_type === "task" ? (s.task_name || "task")
    : s.action_type === "direct_mail" ? (s.mail_piece || "mail")
    : (s.body || s.template_key || "").slice(0, 28);
  return `+${s.delay_value} ${s.delay_unit}${s.delay_value === 1 ? "" : "s"} · ${a}${detail ? ` · ${detail}` : ""}`;
};

const CampaignsPage = () => {
  const notify = useNotify();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [templates, setTemplates] = useState<Tmpl[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [tab, setTab] = useState("lead");
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [open, setOpen] = useState<Record<number, boolean>>({});

  const load = async () => {
    const sb = getSupabaseClient();
    const [{ data: camps }, { data: enr }] = await Promise.all([
      sb.from("drip_campaigns").select("*, drip_steps(*)").order("name"),
      sb.from("drip_enrollments").select("campaign_id").eq("state", "active"),
    ]);
    (camps ?? []).forEach((c: Campaign) => c.drip_steps?.sort((a, b) => a.step_order - b.step_order));
    setCampaigns((camps as Campaign[]) ?? []);
    const cc: Record<string, number> = {};
    (enr ?? []).forEach((e: { campaign_id: string }) => { cc[e.campaign_id] = (cc[e.campaign_id] ?? 0) + 1; });
    setCounts(cc);
  };
  useEffect(() => {
    load();
    getSupabaseClient().from("templates").select("key,name,channel,body,subject").then(({ data }) => setTemplates((data as Tmpl[]) ?? []));
    getSupabaseClient().from("roles").select("id,name").order("id").then(({ data }) => setRoles((data as Role[]) ?? []));
  }, []);

  const edit = (c: Campaign) => { setEditing(JSON.parse(JSON.stringify(c))); setOpen({}); };
  const create = () => setEditing({ id: "", name: "", entity_type: tab, is_active: true, drip_steps: [newStep(1)] });

  const renumber = (steps: Step[]) => steps.map((s, i) => ({ ...s, step_order: i + 1 }));

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim()) { notify("Name is required", { type: "warning" }); return; }
    const sb = getSupabaseClient();
    let campId = editing.id;
    if (campId) {
      const { error } = await sb.from("drip_campaigns").update({ name: editing.name.trim(), is_active: editing.is_active }).eq("id", campId);
      if (error) { notify(`Save failed: ${error.message}`, { type: "error" }); return; }
    } else {
      const { data, error } = await sb.from("drip_campaigns").insert({ name: editing.name.trim(), entity_type: editing.entity_type, is_active: editing.is_active }).select("id").single();
      if (error || !data) { notify(`Create failed: ${error?.message}`, { type: "error" }); return; }
      campId = (data as { id: string }).id;
    }
    // Replace the step set (steps are referenced by step_order, not id — safe).
    await sb.from("drip_steps").delete().eq("campaign_id", campId);
    const rows = renumber(editing.drip_steps).map((s) => ({
      campaign_id: campId, step_order: s.step_order, delay_value: Number(s.delay_value) || 0, delay_unit: s.delay_unit,
      action_type: s.action_type, contact_scope: s.contact_scope,
      template_key: s.template_key || null, body: s.body || null, subject: s.subject || null,
      task_name: s.task_name || null, task_type: s.task_type || null, task_priority: s.task_priority || null, task_role_id: s.task_role_id || null,
      mail_piece: s.mail_piece || null,
      condition: s.condition && s.condition.field ? s.condition : null,
    }));
    if (rows.length) {
      const { error } = await sb.from("drip_steps").insert(rows);
      if (error) { notify(`Steps save failed: ${error.message}`, { type: "error" }); return; }
    }
    notify("Campaign saved", { type: "success" }); setEditing(null); load();
  };

  const del = async (c: Campaign) => {
    if (!window.confirm(`Delete campaign "${c.name}"? Active enrollments will stop.`)) return;
    const { error } = await getSupabaseClient().from("drip_campaigns").delete().eq("id", c.id);
    if (error) notify(`Failed: ${error.message}`, { type: "error" });
    else { notify("Deleted", { type: "success" }); load(); }
  };
  const duplicate = async (c: Campaign) => {
    const sb = getSupabaseClient();
    const { data, error } = await sb.from("drip_campaigns").insert({ name: `${c.name} (copy)`, entity_type: c.entity_type, is_active: false }).select("id").single();
    if (error || !data) { notify(`Duplicate failed: ${error?.message}`, { type: "error" }); return; }
    const rows = (c.drip_steps ?? []).map((s) => ({ ...s, id: undefined, campaign_id: (data as { id: string }).id }));
    if (rows.length) await sb.from("drip_steps").insert(rows.map(({ id: _id, ...r }) => r));
    notify("Duplicated", { type: "success" }); load();
  };
  const togglePause = async (c: Campaign) => {
    await getSupabaseClient().from("drip_campaigns").update({ is_active: !c.is_active }).eq("id", c.id);
    load();
  };

  // ---- editor step ops ----
  const setStep = (i: number, patch: Partial<Step>) =>
    setEditing((e) => e ? { ...e, drip_steps: e.drip_steps.map((s, idx) => idx === i ? { ...s, ...patch } : s) } : e);
  const addStep = () => setEditing((e) => e ? { ...e, drip_steps: renumber([...e.drip_steps, newStep(e.drip_steps.length + 1)]) } : e);
  const dupStep = (i: number) => setEditing((e) => e ? { ...e, drip_steps: renumber([...e.drip_steps.slice(0, i + 1), { ...e.drip_steps[i], id: undefined }, ...e.drip_steps.slice(i + 1)]) } : e);
  const rmStep = (i: number) => setEditing((e) => e ? { ...e, drip_steps: renumber(e.drip_steps.filter((_, idx) => idx !== i)) } : e);
  const onDragEnd = (r: DropResult) => {
    if (!r.destination || !editing) return;
    const arr = [...editing.drip_steps];
    const [moved] = arr.splice(r.source.index, 1);
    arr.splice(r.destination.index, 0, moved);
    setEditing({ ...editing, drip_steps: renumber(arr) });
  };

  const visible = campaigns.filter((c) => c.entity_type === tab);

  if (editing) return (
    <CampaignEditor
      editing={editing} setEditing={setEditing} templates={templates} roles={roles}
      open={open} setOpen={setOpen} onDragEnd={onDragEnd} setStep={setStep}
      addStep={addStep} dupStep={dupStep} rmStep={rmStep} save={save}
    />
  );

  return (
    <div className="p-2 max-w-5xl">
      <h1 className="text-xl font-semibold">Drip campaigns</h1>
      <div className="text-sm text-muted-foreground mb-3">
        Ordered multi-step campaigns. The runner drafts each step for approval (never auto-sent). Assign a campaign to a
        lead status on <a className="underline" href="/settings/automations">Automations</a>, or activate manually from a lead.
      </div>
      <div className="flex gap-1 mb-3">
        {ENTITY_TABS.map((t) => (
          <button key={t.value} disabled={!t.enabled} onClick={() => t.enabled && setTab(t.value)}
            className={`px-3 py-1.5 text-sm rounded ${tab === t.value ? "bg-primary text-primary-foreground" : "bg-background border"} ${!t.enabled ? "opacity-40 cursor-not-allowed" : ""}`}>
            {t.label}{!t.enabled && " (soon)"}
          </button>
        ))}
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Campaigns</CardTitle>
          <Button size="sm" onClick={create}><Plus className="mr-1 h-4 w-4" /> Add drip campaign</Button>
        </CardHeader>
        <CardContent className="space-y-1">
          {visible.map((c) => {
            const by = (a: string) => c.drip_steps?.filter((s) => s.action_type === a).length ?? 0;
            return (
              <div key={c.id} className="flex items-center gap-2 text-sm border-b last:border-0 py-2 flex-wrap">
                <span className="font-medium">{c.name}</span>
                {!c.is_active && <Badge variant="outline" className="text-[10px] text-muted-foreground">paused</Badge>}
                <Badge variant="secondary" className="text-[10px]">{duration(c.drip_steps ?? [])}</Badge>
                <span className="text-xs text-muted-foreground">
                  {by("sms")} SMS · {by("email")} Email · {by("task")} Task · {by("direct_mail")} Mail
                </span>
                <Badge variant="outline" className="text-[10px]">{counts[c.id] ?? 0} active</Badge>
                <span className="ml-auto flex items-center gap-1">
                  <Button size="sm" variant="ghost" onClick={() => edit(c)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => duplicate(c)} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => togglePause(c)}>{c.is_active ? "Pause" : "Activate"}</Button>
                  <Button size="sm" variant="ghost" onClick={() => del(c)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                </span>
              </div>
            );
          })}
          {visible.length === 0 && <div className="text-sm text-muted-foreground py-2">No campaigns yet.</div>}
        </CardContent>
      </Card>
    </div>
  );
};

// ---- editor (separate component to keep the list clean) ---------------------
function CampaignEditor({
  editing, setEditing, templates, roles, open, setOpen, onDragEnd, setStep, addStep, dupStep, rmStep, save,
}: {
  editing: Campaign; setEditing: (c: Campaign | null) => void; templates: Tmpl[]; roles: Role[];
  open: Record<number, boolean>; setOpen: (o: Record<number, boolean>) => void; onDragEnd: (r: DropResult) => void;
  setStep: (i: number, p: Partial<Step>) => void; addStep: () => void; dupStep: (i: number) => void; rmStep: (i: number) => void; save: () => void;
}) {
  const toggle = (i: number) => setOpen({ ...open, [i]: !open[i] });
  return (
    <div className="p-2 max-w-3xl">
      <div className="flex items-center gap-2 mb-3">
        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>← Back</Button>
        <h1 className="text-xl font-semibold">{editing.id ? "Edit campaign" : "New campaign"}</h1>
      </div>
      <Card className="mb-3">
        <CardContent className="flex flex-wrap items-end gap-3 py-3">
          <label className="flex flex-col gap-1 flex-1 min-w-48">
            <span className="text-xs text-muted-foreground">Name</span>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Probate nurture" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Entity</span>
            <Input value="Leads" disabled className="w-24" />
          </label>
          <label className="flex items-center gap-2">
            <Switch checked={editing.is_active} onCheckedChange={(v) => setEditing({ ...editing, is_active: v })} />
            <span className="text-sm">Active</span>
          </label>
        </CardContent>
      </Card>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="steps">
          {(prov) => (
            <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-2">
              {editing.drip_steps.map((s, i) => (
                <Draggable key={i} draggableId={`step-${i}`} index={i}>
                  {(p) => (
                    // draggableProps.style (DraggableStyle) vs React CSSProperties — dnd/React types; runtime-safe cast
                    <div ref={p.innerRef} {...(p.draggableProps as any)}>
                      <Card>
                        <CardContent className="p-2">
                          <div className="flex items-center gap-2">
                            <span {...p.dragHandleProps} className="cursor-grab text-muted-foreground"><GripVertical className="h-4 w-4" /></span>
                            <button onClick={() => toggle(i)} className="flex items-center gap-1 text-sm font-medium">
                              {open[i] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              Step {s.step_order}
                            </button>
                            <span className="text-xs text-muted-foreground truncate">{stepSummary(s)}</span>
                            <span className="ml-auto flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => dupStep(i)} title="Duplicate"><Copy className="h-4 w-4" /></Button>
                              <Button size="sm" variant="ghost" onClick={() => rmStep(i)} title="Delete"><Trash2 className="h-4 w-4" /></Button>
                            </span>
                          </div>
                          {open[i] && <StepFields s={s} i={i} setStep={setStep} templates={templates} roles={roles} />}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </Draggable>
              ))}
              {prov.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="flex items-center gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={addStep}><Plus className="mr-1 h-4 w-4" /> Add step</Button>
        <Button onClick={save} className="ml-auto">Save campaign</Button>
      </div>
    </div>
  );
}

function StepFields({ s, i, setStep, templates, roles }: {
  s: Step; i: number; setStep: (i: number, p: Partial<Step>) => void; templates: Tmpl[]; roles: Role[];
}) {
  const chanTemplates = templates.filter((t) => t.channel === s.action_type);
  const pickTemplate = (key: string) => {
    const t = templates.find((x) => x.key === key);
    setStep(i, { template_key: key || null, body: t ? t.body : s.body, subject: t?.subject ?? s.subject });
  };
  const sel = "border rounded px-2 py-1.5 text-sm bg-background";
  return (
    <div className="mt-2 pl-6 space-y-2">
      <div className="flex gap-2 flex-wrap items-end">
        <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Delay</span>
          <Input type="number" min={0} className="w-20" value={s.delay_value} onChange={(e) => setStep(i, { delay_value: +e.target.value })} /></label>
        <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Unit</span>
          <select className={sel} value={s.delay_unit} onChange={(e) => setStep(i, { delay_unit: e.target.value })}>
            {UNITS.map((u) => <option key={u} value={u}>{u}s</option>)}</select></label>
        <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Action</span>
          <select className={sel} value={s.action_type} onChange={(e) => setStep(i, { action_type: e.target.value })}>
            {ACTIONS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}</select></label>
      </div>

      {(s.action_type === "sms" || s.action_type === "email") && (
        <>
          <div className="flex gap-2 flex-wrap items-end">
            <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Contacts</span>
              <select className={sel} value={s.contact_scope} onChange={(e) => setStep(i, { contact_scope: e.target.value })}>
                <option value="primary">Primary</option><option value="all">All</option></select></label>
            <label className="flex flex-col gap-1 flex-1"><span className="text-[11px] text-muted-foreground">Template</span>
              <select className={sel} value={s.template_key ?? ""} onChange={(e) => pickTemplate(e.target.value)}>
                <option value="">(none / free text)</option>
                {chanTemplates.map((t) => <option key={t.key} value={t.key}>{t.name}</option>)}</select></label>
          </div>
          {s.action_type === "email" && (
            <Input value={s.subject ?? ""} onChange={(e) => setStep(i, { subject: e.target.value })} placeholder="Subject" />
          )}
          <div className="flex flex-wrap gap-1">
            {MERGE_FIELDS.map((m) => (
              <button key={m} type="button" onClick={() => setStep(i, { body: (s.body ?? "") + m })}
                className="text-[11px] rounded border px-1.5 py-0.5 hover:bg-muted font-mono">{m}</button>
            ))}
          </div>
          <Textarea rows={3} value={s.body ?? ""} onChange={(e) => setStep(i, { body: e.target.value })} placeholder="Message body…" />
        </>
      )}

      {s.action_type === "task" && (
        <div className="flex gap-2 flex-wrap items-end">
          <label className="flex flex-col gap-1 flex-1"><span className="text-[11px] text-muted-foreground">Task name</span>
            <Input value={s.task_name ?? ""} onChange={(e) => setStep(i, { task_name: e.target.value })} placeholder="Call the seller" /></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Type</span>
            <Input className="w-24" value={s.task_type ?? ""} onChange={(e) => setStep(i, { task_type: e.target.value })} placeholder="call" /></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Priority</span>
            <select className={sel} value={s.task_priority ?? "medium"} onChange={(e) => setStep(i, { task_priority: e.target.value })}>
              {["low", "medium", "high", "urgent"].map((p) => <option key={p} value={p}>{p}</option>)}</select></label>
          <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Assign role</span>
            <select className={sel} value={s.task_role_id ?? ""} onChange={(e) => setStep(i, { task_role_id: e.target.value ? +e.target.value : null })}>
              <option value="">(unassigned)</option>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></label>
        </div>
      )}

      {s.action_type === "direct_mail" && (
        <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Mail piece</span>
          <select className={sel} value={s.mail_piece ?? "letter"} onChange={(e) => setStep(i, { mail_piece: e.target.value })}>
            {MAIL_PIECES.map((p) => <option key={p} value={p}>{p.replace("_", " ")}</option>)}</select></label>
      )}

      {/* condition — optional skip-guard */}
      <div className="flex gap-2 flex-wrap items-end border-t pt-2">
        <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Only send this step if…</span>
          <select className={sel} value={s.condition?.field ?? ""} onChange={(e) => setStep(i, { condition: e.target.value ? { field: e.target.value, op: "is", value: "" } : null })}>
            {COND_FIELDS.map((f) => <option key={f} value={f}>{f || "(always)"}</option>)}</select></label>
        {s.condition?.field && (
          <>
            <label className="flex flex-col gap-1"><span className="text-[11px] text-muted-foreground">Op</span>
              <select className={sel} value={s.condition.op ?? "is"} onChange={(e) => setStep(i, { condition: { ...s.condition, op: e.target.value } })}>
                {(s.condition.field === "tag" ? ["present", "absent"] : ["is", "is_not"]).map((o) => <option key={o} value={o}>{o}</option>)}</select></label>
            {!["responded", "contactable"].includes(s.condition.field) && (
              <label className="flex flex-col gap-1 flex-1"><span className="text-[11px] text-muted-foreground">Value</span>
                <Input value={s.condition.value ?? ""} onChange={(e) => setStep(i, { condition: { ...s.condition, value: e.target.value } })}
                  placeholder={s.condition.field === "status" ? "nurture" : s.condition.field === "temperature" ? "warm" : s.condition.field === "tag" ? "tag id" : ""} /></label>
            )}
          </>
        )}
      </div>
    </div>
  );
}

CampaignsPage.path = "/settings/campaigns";
export default CampaignsPage;
